import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const location = useLocation();

  // =====================================================
  // STICKY HEADER SCROLL EFFECT
  // =====================================================
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    // Check initial scroll position
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // =====================================================
  // CLOSE MOBILE MENU WHEN ROUTE CHANGES
  // =====================================================
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // =====================================================
  // NAVIGATION LINKS
  // =====================================================
  const navLinks = [
    {
      name: "Home",
      path: "/",
    },
    {
      name: "Reservation",
      path: "/reservation",
    },
    {
      name: "Menu",
      path: "/menu",
    },
    {
      name: "Gallery",
      path: "/gallery",
    },
    {
      name: "About Us",
      path: "/about",
    },
    {
      name: "Contact",
      path: "/contact",
    },
  ];

  // =====================================================
  // CLOSE MOBILE MENU
  // =====================================================
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* =====================================================
          STICKY HEADER
      ===================================================== */}
      <header
        className={`
          sticky
          top-0
          z-50
          w-full
          transition-all
          duration-500
          ease-in-out
          ${
            isScrolled
              ? "bg-white/80 shadow-lg backdrop-blur-xl"
              : "bg-white"
          }
        `}
      >
        {/* =====================================================
            HEADER CONTAINER
        ===================================================== */}
        <div className="mx-auto max-w-[1700px] px-5 sm:px-8 lg:px-16">
          <div className="flex h-[90px] items-center justify-between md:h-[100px]">

            {/* =================================================
                LOGO
            ================================================= */}
            <Link
              to="/"
              onClick={closeMobileMenu}
              className="flex shrink-0 items-center"
              aria-label="Go to homepage"
            >
              <img
                src="/capeesh1.png"
                alt="Capeesh Cafe Kitchen & Grill"
                className="
                  w-[125px]
                  object-contain
                  transition-all
                  duration-300
                  hover:scale-105
                  sm:w-[140px]
                  md:w-[150px]
                "
              />
            </Link>

            {/* =================================================
                DESKTOP NAVIGATION
            ================================================= */}
            <nav className="hidden items-center gap-7 md:flex lg:gap-10">
              {navLinks.map((link) => {
                const isActive =
                  location.pathname === link.path;

                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`
                      relative
                      py-2
                      text-[15px]
                      font-medium
                      transition-all
                      duration-300

                      ${
                        isActive
                          ? "text-black"
                          : "text-gray-800 hover:text-black"
                      }

                      after:absolute
                      after:bottom-0
                      after:left-0
                      after:h-[2px]
                      after:bg-black
                      after:transition-all
                      after:duration-300

                      ${
                        isActive
                          ? "after:w-full"
                          : "after:w-0 hover:after:w-full"
                      }
                    `}
                  >
                    {link.name}
                  </Link>
                );
              })}
            </nav>

            {/* =================================================
                MOBILE MENU BUTTON
            ================================================= */}
            <button
              type="button"
              onClick={() =>
                setIsMobileMenuOpen(!isMobileMenuOpen)
              }
              className="
                flex
                h-11
                w-11
                items-center
                justify-center
                rounded-full
                text-gray-900
                transition-all
                duration-300
                hover:bg-gray-100
                md:hidden
              "
              aria-label={
                isMobileMenuOpen
                  ? "Close menu"
                  : "Open menu"
              }
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X size={25} strokeWidth={2} />
              ) : (
                <Menu size={25} strokeWidth={2} />
              )}
            </button>
          </div>
        </div>

        {/* =====================================================
            MOBILE NAVIGATION
        ===================================================== */}
        <div
          className={`
            overflow-hidden
            border-t
            border-gray-200
            bg-white
            transition-all
            duration-500
            ease-in-out
            md:hidden

            ${
              isMobileMenuOpen
                ? "max-h-[700px] opacity-100"
                : "max-h-0 opacity-0"
            }
          `}
        >
          <nav className="flex flex-col px-6 py-4">

            {navLinks.map((link) => {
              const isActive =
                location.pathname === link.path;

              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={closeMobileMenu}
                  className={`
                    border-b
                    border-gray-100
                    py-4
                    text-[16px]
                    font-medium
                    transition-all
                    duration-300

                    ${
                      isActive
                        ? "text-black"
                        : "text-gray-700 hover:text-black"
                    }
                  `}
                >
                  {link.name}
                </Link>
              );
            })}

          </nav>
        </div>
      </header>
    </>
  );
};

export default Header;

